import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'

const nullableString = z.string().nullable().optional()
const nullableNumber = z.number().nullable().optional()

const managerMembershipSchema = z
  .object({
    status: nullableString,
    role: nullableString,
    joined_at: nullableString,
  })
  .nullable()
  .optional()

const managerPlayerSchema = z
  .object({
    slot: nullableString,
    slot_index: nullableNumber,
    player_id: z.number().optional(),
    name: nullableString,
    image_path: nullableString,
    team_id: nullableNumber,
    team_name: nullableString,
    team_display_name: nullableString,
    position_group: nullableString,
    jersey_number: nullableNumber,
    is_new_in_round: z.boolean().optional(),
    original_player_id: nullableNumber,
    points: z.number().optional(),
    potential_points: nullableNumber,
    in_current_squad: z.boolean().optional(),
    selection_count: nullableNumber,
    is_selected: z.boolean().optional(),
    selection_block_reasons: z.array(z.string()).optional().default([]),
    is_selectable: z.boolean().optional(),
    selection_status_message: nullableString,
  })
  .passthrough()

const managerPlayerRankingPlayerSchema = z
  .object({
    player_id: z.number(),
    name: nullableString,
    image_path: nullableString,
    team_id: nullableNumber,
    team_name: nullableString,
    team_display_name: nullableString,
    position_group: nullableString,
    jersey_number: nullableNumber,
    total_points: z.number().optional().default(0),
    matches_count: z.number().optional().default(0),
    finished_matches_count: z.number().optional().default(0),
    rank: z.number().optional().default(0),
  })
  .passthrough()

const managerPlayerRankingResponseSchema = z
  .object({
    league: z
      .object({
        id: z.number().optional(),
        name: nullableString,
        season_id: nullableNumber,
      })
      .passthrough(),
    filters: z
      .object({
        q: nullableString,
        team_id: nullableNumber,
        slot: nullableString,
      })
      .passthrough(),
    players: z.array(managerPlayerRankingPlayerSchema).optional().default([]),
    pagination: z
      .object({
        current_page: z.number().optional().default(1),
        per_page: z.number().optional().default(50),
        total: z.number().optional().default(0),
        last_page: z.number().optional().default(1),
      })
      .passthrough(),
  })
  .passthrough()

const managerSuggestionPlayerSchema = managerPlayerSchema
  .extend({
    recent_points: z.number().optional(),
    recent_matches_count: z.number().optional(),
    season_points: z.number().optional(),
    matches_count: z.number().optional(),
  })
  .passthrough()

const managerTeamSchema = z
  .object({
    id: z.number(),
    name: nullableString,
    display_name: nullableString,
    image_path: nullableString,
  })
  .passthrough()

const managerPlayerFiltersSchema = z
  .object({
    slot: nullableString,
    q: nullableString,
    team_id: nullableNumber,
    sort: nullableString,
    replace_player_id: nullableNumber,
    has_active_filters: z.boolean().optional(),
  })
  .passthrough()

const managerPlayersResponseSchema = z
  .object({
    players: z.array(managerPlayerSchema).optional().default([]),
    teams: z.array(managerTeamSchema).optional().default([]),
    filters: managerPlayerFiltersSchema.nullable().optional(),
  })
  .passthrough()

const managerLockedSlotSchema = z
  .object({
    slot: z.string(),
    slot_index: z.number(),
    player_id: z.number(),
  })
  .passthrough()

const managerFormationSchema = z
  .object({
    key: nullableString,
    code: nullableString,
    label: nullableString,
    lineup_size: z.number().optional(),
    slots: z.record(z.string(), z.number()).optional(),
    is_default: z.boolean().optional(),
  })
  .passthrough()

const managerLineupSchema = z
  .object({
    default_formation: nullableString,
    selected_formation: nullableString,
    lineup_size: z.number().optional(),
    selected_slots_count: z.number().optional(),
    entry_completed: z.boolean().optional(),
    submission_required: z.boolean().optional(),
    is_implied_entry: z.boolean().optional(),
    can_save_draft: z.boolean().optional(),
    is_submitted: z.boolean().optional(),
    swaps_allowed: z.number().optional(),
    swaps_used: z.number().optional(),
    swaps_remaining: z.number().optional(),
    team_rules: z
      .object({
        max_players_per_club: nullableNumber,
        min_distinct_clubs: nullableNumber,
        effective_max_players_per_club: nullableNumber,
        effective_min_distinct_clubs: nullableNumber,
        available_club_count: nullableNumber,
        swaps_per_round: nullableNumber,
      })
      .passthrough()
      .optional(),
    locked_player_ids: z.array(z.number()).optional().default([]),
    locked_slots: z.array(managerLockedSlotSchema).optional().default([]),
    can_create_base_team: z.boolean().optional(),
    position_groups: z.array(z.string()).optional().default([]),
    formations: z.array(managerFormationSchema).optional().default([]),
  })
  .passthrough()

const managerEntrySchema = z
  .object({
    formation: nullableString,
    players: z.array(managerPlayerSchema).optional().default([]),
  })
  .passthrough()

const managerRoundSchema = z
  .object({
    id: z.number().optional(),
    name: nullableString,
    sequence: z.number().optional(),
    lock_at: nullableString,
    is_locked: z.boolean().optional(),
  })
  .passthrough()

const managerRankingRoundSchema = managerRoundSchema
  .extend({
    matches_count: z.number().optional(),
    played_matches_count: z.number().optional(),
    completed_matches_count: z.number().optional(),
  })
  .passthrough()

const managerRoundsResponseSchema = z
  .object({
    league: z
      .object({
        id: z.number().optional(),
        name: nullableString,
        season_id: z.number().optional(),
      })
      .passthrough()
      .optional(),
    rounds: z.array(managerRankingRoundSchema).optional().default([]),
  })
  .passthrough()

const managerRankingRoundScoreSchema = z
  .object({
    app_round_id: z.number(),
    points: z.number().optional().default(0),
    is_final: z.boolean().optional().default(false),
    has_entry: z.boolean().optional().default(false),
  })
  .passthrough()

const managerRankingEntrySchema = z
  .object({
    user_id: z.number(),
    display_name: nullableString,
    profile_image: nullableString,
    total_points: z.number().optional().default(0),
    rounds_played: z.number().optional().default(0),
    round_scores: z.array(managerRankingRoundScoreSchema).optional().default([]),
    rank: z.number().optional().default(0),
  })
  .passthrough()

const managerRankingResponseSchema = z
  .object({
    league: z
      .object({
        id: z.number().optional(),
        name: nullableString,
      })
      .passthrough()
      .optional(),
    ranking: z.array(managerRankingEntrySchema).optional().default([]),
  })
  .passthrough()

const managerRoundRankingResponseSchema = z
  .object({
    round: managerRankingRoundSchema,
    ranking: z
      .array(
        z
          .object({
            user_id: z.number(),
            display_name: nullableString,
            profile_image: nullableString,
            points: z.number().optional().default(0),
            has_entry: z.boolean().optional().default(false),
            entry_completed: z.boolean().optional().default(false),
            rank: z.number().optional().default(0),
          })
          .passthrough(),
      )
      .optional()
      .default([]),
  })
  .passthrough()

const managerLineupSuggestionSchema = z
  .object({
    slot: z.string(),
    slot_index: z.number(),
    points_delta: z.number(),
    reason_code: nullableString,
    outgoing: managerSuggestionPlayerSchema,
    incoming: managerSuggestionPlayerSchema,
  })
  .passthrough()

const managerLineupSuggestionsSchema = z
  .object({
    league_id: z.number().optional(),
    app_round: managerRoundSchema.optional(),
    basis: z
      .object({
        type: nullableString,
        window_rounds: z.number().optional(),
        points_field: nullableString,
        minimum_points_delta: z.number().optional(),
        max_suggestions: z.number().optional(),
      })
      .passthrough()
      .optional()
      .default({}),
    lineup: z
      .object({
        formation: nullableString,
        swaps_allowed: z.number().optional(),
        swaps_used: z.number().optional(),
        swaps_remaining: z.number().optional(),
      })
      .passthrough()
      .optional()
      .default({}),
    reason: z.string().optional().default('unknown'),
    suggestions: z.array(managerLineupSuggestionSchema).optional().default([]),
  })
  .passthrough()

const managerRoundEditabilitySchema = z
  .object({
    can_edit: z.boolean().optional(),
    status: nullableString,
    reason: nullableString,
    expected_unlock_at: nullableString,
  })
  .passthrough()

const managerRoundDetailSchema = z
  .object({
    app_round: z
      .object({
        id: z.number().optional(),
        name: nullableString,
        sequence: z.number().optional(),
        fantasy: z
          .object({
            is_locked: z.boolean().optional(),
            has_matches: z.boolean().optional(),
            editability: managerRoundEditabilitySchema.nullable().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
  })
  .passthrough()

const managerRoundAvailabilitySchema = z
  .object({
    league_id: z.number().optional(),
    app_round: managerRoundSchema,
    lineup: managerLineupSchema.optional(),
    entry: managerEntrySchema.nullable().optional(),
    draft: z
      .object({
        formation: nullableString,
        players: z.array(managerPlayerSchema).optional().default([]),
        draft_version: z.number().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough()

const seasonSchema = z
  .object({
    id: z.number().optional(),
    name: nullableString,
  })
  .passthrough()

const competitionSchema = z
  .object({
    id: z.number().optional(),
    name: z.string().optional(),
    image_path: nullableString,
    logo_url: nullableString,
  })
  .passthrough()

const managerLeagueDetailSchema = z
  .object({
    league: z
      .object({
        id: z.number().optional(),
        name: z.string().optional(),
        season: seasonSchema.nullable().optional(),
        competition: competitionSchema.nullable().optional(),
        icons: z.unknown().optional(),
        visibility: nullableString,
        requires_approval: z.boolean().optional(),
        enabled: z.boolean().optional(),
        is_closed: z.boolean().optional(),
        members_count: z.number().optional(),
        membership: managerMembershipSchema,
      })
      .passthrough(),
  })
  .passthrough()

const publicManagerLeagueSchema = z
  .object({
    id: z.number(),
    season_id: nullableNumber,
    season_name: nullableString,
    name: z.string(),
    description: nullableString,
    competition: competitionSchema.nullable().optional(),
    icons: z.unknown().optional(),
    type: nullableString,
    visibility: nullableString,
    requires_approval: z.boolean().optional(),
    enabled: z.boolean().optional(),
    is_closed: z.boolean().optional(),
    members_count: z.number().optional(),
    my_membership: managerMembershipSchema,
  })
  .passthrough()

const publicManagerLeagueResponseSchema = z
  .object({
    data: z.array(publicManagerLeagueSchema).optional().default([]),
    current_page: nullableNumber,
    last_page: nullableNumber,
    total: nullableNumber,
  })
  .passthrough()

const bestLeagueSchema = z
  .object({
    fantasy_league_id: z.number().optional(),
    league_name: z.string().optional(),
    points: z.number().optional(),
    rank: nullableNumber,
    total_members: nullableNumber,
    season: seasonSchema.nullable().optional(),
    competition: competitionSchema.nullable().optional(),
    best_player: managerPlayerSchema.nullable().optional(),
  })
  .passthrough()

const managerOverviewSchema = z
  .object({
    total_points: z.number().optional(),
    current_round_points: z.number().optional(),
    active_leagues_count: z.number().optional(),
    best_player: managerPlayerSchema.nullable().optional(),
    best_league: bestLeagueSchema.nullable().optional(),
  })
  .passthrough()

const summarySchema = z
  .object({
    joined_leagues_count: z.number().optional(),
    active_leagues_count: z.number().optional(),
    teams_needing_setup_count: z.number().optional(),
    teams_with_changes_available_count: z.number().optional(),
    next_deadline_at: nullableString,
    deadlines_due_today_count: z.number().optional(),
    pending_actions_count: z.number().optional(),
  })
  .passthrough()

const primaryActionSchema = z
  .object({
    type: z.string().optional(),
    fantasy_league_id: z.number().optional(),
    app_round_id: z.number().optional(),
    label: z.string().optional(),
    lock_at: nullableString,
  })
  .passthrough()

const pendingActionSchema = z
  .object({
    type: z.string().optional(),
    fantasy_league_id: z.number().optional(),
    app_round_id: z.number().optional(),
    league_name: z.string().optional(),
    status_key: z.string().optional(),
    lock_at: nullableString,
    season: seasonSchema.nullable().optional(),
    competition: competitionSchema.nullable().optional(),
  })
  .passthrough()

const managerHomeLeagueSchema = z
  .object({
    fantasy_league_id: z.number().optional(),
    league_name: z.string().optional(),
    season: seasonSchema.nullable().optional(),
    competition: competitionSchema.nullable().optional(),
    next_round: managerRoundSchema.nullable().optional(),
    status: z
      .object({
        key: nullableString,
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

const managerLeaguePreviewSchema = z
  .object({
    fantasy_league_id: z.number().optional(),
    league_name: z.string().optional(),
    season: seasonSchema.nullable().optional(),
    competition: competitionSchema.nullable().optional(),
    status: z
      .object({
        key: nullableString,
      })
      .passthrough()
      .nullable()
      .optional(),
    lineup: z
      .object({
        has_entry: z.boolean().optional(),
        entry_completed: z.boolean().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

const leagueRoundSchema = z
  .object({
    id: z.number().optional(),
    name: z.string().optional(),
    sequence: z.number().optional(),
  })
  .passthrough()

const leagueWinnerSchema = z
  .object({
    user_id: z.number().optional(),
    name: nullableString,
    profile_image: nullableString,
    points: z.number().optional(),
    rank: nullableNumber,
  })
  .passthrough()

const managerContributionLeagueSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    season_id: nullableNumber,
  })
  .passthrough()

const managerPlayerContributionTotalSchema = z
  .object({
    player_id: z.number(),
    name: z.string(),
    image_path: nullableString,
    team_id: nullableNumber,
    team_name: nullableString,
    team_display_name: nullableString,
    jersey_number: nullableNumber,
    position_group: nullableString,
    total_points: z.number().optional().default(0),
    rounds_count: z.number().optional().default(0),
    best_round_points: z.number().optional().default(0),
  })
  .passthrough()

const managerPlayerContributionRoundPlayerSchema = z
  .object({
    player_id: z.number(),
    name: z.string(),
    image_path: nullableString,
    team_id: nullableNumber,
    team_name: nullableString,
    team_display_name: nullableString,
    jersey_number: nullableNumber,
    position_group: nullableString,
    slot: nullableString,
    slot_index: nullableNumber,
    points: z.number().optional().default(0),
  })
  .passthrough()

const managerPlayerContributionRoundSchema = z
  .object({
    app_round: leagueRoundSchema,
    total_points: z.number().optional().default(0),
    players: z.array(managerPlayerContributionRoundPlayerSchema).optional().default([]),
  })
  .passthrough()

const managerPlayerContributionsResponseSchema = z
  .object({
    league: managerContributionLeagueSchema,
    totals: z
      .object({
        players: z.array(managerPlayerContributionTotalSchema).optional().default([]),
      })
      .passthrough(),
    rounds: z.array(managerPlayerContributionRoundSchema).optional().default([]),
  })
  .passthrough()

const managerPlayerContributionDetailRoundSchema = z
  .object({
    app_round: leagueRoundSchema,
    slot: nullableString,
    slot_index: nullableNumber,
    points: z.number().optional().default(0),
  })
  .passthrough()

const managerPlayerContributionDetailSchema = z
  .object({
    league: managerContributionLeagueSchema,
    player: z
      .object({
        player_id: z.number(),
        name: z.string(),
        image_path: nullableString,
        team_id: nullableNumber,
        team_name: nullableString,
        team_display_name: nullableString,
        position_group: nullableString,
        jersey_number: nullableNumber,
        total_points: z.number().optional().default(0),
        selected_rounds_count: z.number().optional().default(0),
        best_round_points: z.number().optional().default(0),
      })
      .passthrough(),
    rounds: z.array(managerPlayerContributionDetailRoundSchema).optional().default([]),
  })
  .passthrough()

const managerBreakdownTeamSchema = z
  .object({
    id: nullableNumber,
    name: nullableString,
    display_name: nullableString,
    short_name: nullableString,
  })
  .passthrough()

const managerPlayerRoundBreakdownMatchSchema = z
  .object({
    match: z
      .object({
        id: z.number(),
        kickoff: nullableString,
        status: nullableString,
        home_team: managerBreakdownTeamSchema,
        away_team: managerBreakdownTeamSchema,
      })
      .passthrough(),
    breakdown: z
      .object({
        minutes_played: z.number().optional().default(0),
        minutes_points: z.number().optional().default(0),
        goals: z.number().optional().default(0),
        goal_points: z.number().optional().default(0),
        assists: z.number().optional().default(0),
        assist_points: z.number().optional().default(0),
        yellow_cards: z.number().optional().default(0),
        yellow_card_points: z.number().optional().default(0),
        red_cards: z.number().optional().default(0),
        red_card_points: z.number().optional().default(0),
        own_goals: z.number().optional().default(0),
        own_goal_points: z.number().optional().default(0),
        clean_sheet: z.boolean().optional().default(false),
        clean_sheet_points: z.number().optional().default(0),
        saves: z.number().optional().default(0),
        saves_points: z.number().optional().default(0),
        penalties_saved: z.number().optional().default(0),
        penalties_saved_points: z.number().optional().default(0),
        key_passes: z.number().optional().default(0),
        key_passes_points: z.number().optional().default(0),
        total: z.number().optional().default(0),
      })
      .passthrough(),
    stored_points: nullableNumber,
    stored_is_final: z.boolean().nullable().optional(),
  })
  .passthrough()

const managerPlayerRoundBreakdownSchema = z
  .object({
    league: managerContributionLeagueSchema,
    app_round: leagueRoundSchema,
    player: z
      .object({
        player_id: z.number(),
        name: z.string(),
        image_path: nullableString,
        team_id: nullableNumber,
        team_name: nullableString,
        team_display_name: nullableString,
        position_group: nullableString,
        jersey_number: nullableNumber,
        round_points: z.number().optional().default(0),
      })
      .passthrough(),
    matches: z.array(managerPlayerRoundBreakdownMatchSchema).optional().default([]),
  })
  .passthrough()

const managerLeaguePlayerContributionRoundSchema = z
  .object({
    app_round: leagueRoundSchema,
    round_points: z.number().optional().default(0),
    matches: z.array(managerPlayerRoundBreakdownMatchSchema).optional().default([]),
  })
  .passthrough()

const managerLeaguePlayerContributionSchema = z
  .object({
    league: managerContributionLeagueSchema,
    player: z
      .object({
        player_id: z.number(),
        name: z.string(),
        image_path: nullableString,
        team_id: nullableNumber,
        team_name: nullableString,
        team_display_name: nullableString,
        position_group: nullableString,
        jersey_number: nullableNumber,
        total_points: z.number().optional().default(0),
        matches_count: z.number().optional().default(0),
        finished_matches_count: z.number().optional().default(0),
        best_round_points: z.number().optional().default(0),
      })
      .passthrough(),
    rounds: z.array(managerLeaguePlayerContributionRoundSchema).optional().default([]),
  })
  .passthrough()

const managerLastRoundSquadPlayerSchema = z
  .object({
    player_id: z.number(),
    name: nullableString,
    image_path: nullableString,
    club: nullableString,
    slot: nullableString,
    points: z.number(),
    form_points: z.number().optional().default(0),
    recent_points: z.array(z.number()).optional().default([]),
    recent_form: z
      .array(
        z
          .object({
            round_id: z.number(),
            sequence: z.number().optional().default(0),
            name: z.string().optional().default(''),
            points: z.number().optional().default(0),
            has_entry: z.boolean().optional().default(false),
          })
          .passthrough(),
      )
      .optional()
      .default([]),
  })
  .passthrough()

const leagueDashboardSchema = z
  .object({
    league: z
      .object({
        id: z.number().optional(),
        season_id: nullableNumber,
        name: z.string().optional(),
      })
      .passthrough(),
    dashboard: z
      .object({
        total_points: z.number().optional(),
        current_round_points: z.number().optional(),
        current_round: leagueRoundSchema.nullable().optional(),
        my_rank: nullableNumber,
        total_members: z.number().optional(),
        best_players: z
          .object({
            total: managerPlayerSchema.nullable().optional(),
            current_round: managerPlayerSchema.nullable().optional(),
          })
          .nullable()
          .optional(),
        last_round_squad: z.array(managerLastRoundSquadPlayerSchema).optional().default([]),
      })
      .passthrough(),
    round_winner: z
      .object({
        winner_round: leagueRoundSchema.nullable().optional(),
        winner: leagueWinnerSchema.nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough()

const managerHomeSchema = z
  .object({
    summary: summarySchema,
    manager_overview: managerOverviewSchema.optional(),
    primary_action: primaryActionSchema.nullable().optional(),
    pending_actions: z.array(pendingActionSchema).optional().default([]),
    my_leagues: z.array(managerHomeLeagueSchema).optional().default([]),
    manager_league_previews: z.array(managerLeaguePreviewSchema).optional().default([]),
    manager_league_previews_total: z.number().optional().default(0),
  })
  .passthrough()

const managerNextLineupSchema = z
  .object({
    league: z.object({ id: z.number().optional() }).passthrough(),
    lineup_flow: z
      .object({
        can_edit: z.boolean().optional(),
        app_round: managerRoundSchema.nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough()

const managerShareSchema = z
  .object({
    type: z.string(),
    title: z.string(),
    share_url: z.string(),
    app_url: z.string(),
    share_message: z.string(),
    resource: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough()

const managerSubleagueSchema = z
  .object({
    id: z.number(),
    fantasy_league_id: z.number(),
    name: z.string(),
    slug: nullableString,
    visibility: nullableString,
    members_count: z.number().optional().default(0),
    created_at: nullableString,
    creator: z
      .object({
        id: z.number().optional(),
        display_name: nullableString,
        profile_image: nullableString,
      })
      .nullable()
      .optional(),
    my_membership: z
      .object({
        role: nullableString,
        joined_at: nullableString,
      })
      .nullable()
      .optional(),
    can_manage: z.boolean().optional().default(false),
  })
  .passthrough()

const managerSubleagueListResponseSchema = z
  .object({
    league: z
      .object({
        id: z.number(),
        name: z.string(),
        season_id: z.number(),
      })
      .passthrough(),
    subleagues: z.array(managerSubleagueSchema).optional().default([]),
    meta: z
      .object({
        can_create_subleague: z.boolean().optional().default(false),
      })
      .passthrough(),
  })
  .passthrough()

const managerSubleagueCreateResponseSchema = z
  .object({
    success: z.boolean().optional(),
    league: z.record(z.string(), z.unknown()).optional(),
    subleague: managerSubleagueSchema,
  })
  .passthrough()

const managerSubleagueInviteCreateResponseSchema = z
  .object({
    invite_url: z.string(),
    token_expires_at: nullableString,
    max_uses: nullableNumber,
  })
  .passthrough()

export type ManagerPlayer = z.infer<typeof managerPlayerSchema>
export type ManagerPlayerRankingResponse = z.infer<typeof managerPlayerRankingResponseSchema>
export type ManagerPlayerRankingPlayer = z.infer<typeof managerPlayerRankingPlayerSchema>
export type ManagerBestLeague = z.infer<typeof bestLeagueSchema>
export type ManagerHome = z.infer<typeof managerHomeSchema>
export type ManagerPendingAction = z.infer<typeof pendingActionSchema>
export type ManagerHomeLeague = z.infer<typeof managerHomeLeagueSchema>
export type ManagerLeaguePreview = z.infer<typeof managerLeaguePreviewSchema>
export type ManagerLeagueDashboard = z.infer<typeof leagueDashboardSchema>
export type ManagerLastRoundSquadPlayer = z.infer<typeof managerLastRoundSquadPlayerSchema>
export type ManagerFormation = z.infer<typeof managerFormationSchema>
export type ManagerLineup = z.infer<typeof managerLineupSchema>
export type ManagerRoundAvailability = z.infer<typeof managerRoundAvailabilitySchema>
export type ManagerNextLineup = z.infer<typeof managerNextLineupSchema>
export type ManagerShare = z.infer<typeof managerShareSchema>
export type ManagerSubleague = z.infer<typeof managerSubleagueSchema>
export type ManagerSubleagueListResponse = z.infer<typeof managerSubleagueListResponseSchema>
export type ManagerSubleagueInvite = {
  inviteUrl: string
  tokenExpiresAt?: string | null | undefined
  maxUses?: number | null | undefined
}
export type ManagerTeam = z.infer<typeof managerTeamSchema>
export type ManagerPlayersResponse = z.infer<typeof managerPlayersResponseSchema>
export type ManagerRoundDetail = z.infer<typeof managerRoundDetailSchema>
export type ManagerLeagueDetail = z.infer<typeof managerLeagueDetailSchema>
export type PublicManagerLeague = z.infer<typeof publicManagerLeagueSchema>
export type PublicManagerLeagueResponse = z.infer<typeof publicManagerLeagueResponseSchema>
export type ManagerRoundsResponse = z.infer<typeof managerRoundsResponseSchema>
export type ManagerRankingEntry = z.infer<typeof managerRankingEntrySchema>
export type ManagerRankingResponse = z.infer<typeof managerRankingResponseSchema>
export type ManagerRoundRankingResponse = z.infer<typeof managerRoundRankingResponseSchema>
export type ManagerLineupSuggestion = z.infer<typeof managerLineupSuggestionSchema>
export type ManagerLineupSuggestions = z.infer<typeof managerLineupSuggestionsSchema>
export type ManagerPlayerContributions = z.infer<typeof managerPlayerContributionsResponseSchema>
export type ManagerPlayerContributionTotal = z.infer<typeof managerPlayerContributionTotalSchema>
export type ManagerPlayerContributionRound = z.infer<typeof managerPlayerContributionRoundSchema>
export type ManagerPlayerContributionRoundPlayer = z.infer<typeof managerPlayerContributionRoundPlayerSchema>
export type ManagerPlayerContributionDetail = z.infer<typeof managerPlayerContributionDetailSchema>
export type ManagerPlayerRoundBreakdown = z.infer<typeof managerPlayerRoundBreakdownSchema>
export type ManagerLeaguePlayerContribution = z.infer<typeof managerLeaguePlayerContributionSchema>

async function fetchManagerHome(): Promise<ManagerHome> {
  const response = await api.get<unknown>('/fantasy-leagues/home')
  const parsed = managerHomeSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid home payload', parsed.error.flatten())
    throw new Error('Golero Manager kon niet worden geladen.')
  }

  return parsed.data
}

async function fetchPublicManagerLeagues(query: string, page: number): Promise<PublicManagerLeagueResponse> {
  const params = new URLSearchParams({ type: 'official', page: String(page), per_page: '20' })
  if (query.trim()) params.set('q', query.trim())

  const response = await api.get<unknown>(`/fantasy-leagues?${params.toString()}`)
  const parsed = publicManagerLeagueResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid public league payload', parsed.error.flatten())
    throw new Error('Manager-leagues konden niet worden geladen.')
  }

  return parsed.data
}

export function usePublicManagerLeagues(query = '') {
  const normalizedQuery = query.trim()

  return useInfiniteQuery({
    queryKey: ['public-manager-leagues', normalizedQuery],
    queryFn: ({ pageParam }) => fetchPublicManagerLeagues(normalizedQuery, Number(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.current_page ?? 1
      const lastPageNumber = lastPage.last_page ?? currentPage

      return currentPage < lastPageNumber ? currentPage + 1 : undefined
    },
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

export function useManagerHome(enabled = true) {
  return useQuery({
    queryKey: ['manager-home'],
    queryFn: fetchManagerHome,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

async function fetchManagerLeagueDashboard(leagueId: number): Promise<ManagerLeagueDashboard> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/dashboard`)
  const parsed = leagueDashboardSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid league dashboard payload', parsed.error.flatten())
    throw new Error('De Manager-competitie kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerLeagueDashboard(leagueId: number | undefined) {
  return useQuery({
    queryKey: ['manager-league-dashboard', leagueId],
    queryFn: () => fetchManagerLeagueDashboard(leagueId!),
    enabled: Boolean(leagueId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

async function fetchManagerLeagueDetail(leagueId: number): Promise<ManagerLeagueDetail> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}`)
  const parsed = managerLeagueDetailSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid league detail payload', parsed.error.flatten())
    throw new Error('De Manager-competitie kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerLeagueDetail(leagueId: number | undefined) {
  return useQuery({
    queryKey: ['manager-league-detail', leagueId],
    queryFn: () => fetchManagerLeagueDetail(leagueId!),
    enabled: Boolean(leagueId),
    staleTime: 60_000,
  })
}

async function fetchManagerLeagueShare(leagueId: number): Promise<ManagerShare> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/share`)
  const parsed = managerShareSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid league share payload', parsed.error.flatten())
    throw new Error('De Manager-sharelink kon niet worden gemaakt.')
  }

  return parsed.data
}

export function useShareManagerLeague(leagueId: number | undefined) {
  return useMutation({
    mutationFn: () => fetchManagerLeagueShare(leagueId as number),
  })
}

async function fetchManagerSubleagues(leagueId: number): Promise<ManagerSubleagueListResponse> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/subleagues`)
  const parsed = managerSubleagueListResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid subleague payload', parsed.error.flatten())
    throw new Error('De Manager-subleagues konden niet worden geladen.')
  }

  return parsed.data
}

export function useManagerSubleagues(leagueId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['manager-subleagues', leagueId],
    queryFn: () => fetchManagerSubleagues(leagueId as number),
    enabled: Boolean(leagueId) && enabled,
    staleTime: 30_000,
  })
}

async function createManagerSubleague(leagueId: number, name: string): Promise<ManagerSubleague> {
  const response = await api.post<unknown>(`/fantasy-leagues/${leagueId}/subleagues`, { name })
  const parsed = managerSubleagueCreateResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid created subleague payload', parsed.error.flatten())
    throw new Error('De Manager-subleague kon niet worden aangemaakt.')
  }

  return parsed.data.subleague
}

export function useCreateManagerSubleague(leagueId: number | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => createManagerSubleague(leagueId as number, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['manager-subleagues', leagueId] })
    },
  })
}

async function fetchManagerSubleagueRanking(leagueId: number, subleagueId: number): Promise<ManagerRankingResponse> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/subleagues/${subleagueId}/ranking`)
  const parsed = managerRankingResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid subleague ranking payload', parsed.error.flatten())
    throw new Error('De subleague-ranglijst kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerSubleagueRanking(
  leagueId: number | undefined,
  subleagueId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-subleague-ranking', leagueId, subleagueId],
    queryFn: () => fetchManagerSubleagueRanking(leagueId as number, subleagueId as number),
    enabled: Boolean(leagueId) && Boolean(subleagueId) && enabled,
    staleTime: 30_000,
  })
}

async function fetchManagerSubleagueRoundRanking(
  leagueId: number,
  subleagueId: number,
  roundId: number,
): Promise<ManagerRoundRankingResponse> {
  const response = await api.get<unknown>(
    `/fantasy-leagues/${leagueId}/subleagues/${subleagueId}/app-rounds/${roundId}/ranking`,
  )
  const parsed = managerRoundRankingResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid subleague round ranking payload', parsed.error.flatten())
    throw new Error('De ranking van deze subleague-speelronde kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerSubleagueRoundRanking(
  leagueId: number | undefined,
  subleagueId: number | undefined,
  roundId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-subleague-round-ranking', leagueId, subleagueId, roundId],
    queryFn: () => fetchManagerSubleagueRoundRanking(leagueId!, subleagueId!, roundId!),
    enabled: Boolean(leagueId && subleagueId && roundId && enabled),
    staleTime: 15_000,
  })
}

async function createManagerSubleagueInvite(leagueId: number, subleagueId: number): Promise<ManagerSubleagueInvite> {
  const response = await api.post<unknown>(`/fantasy-leagues/${leagueId}/subleagues/${subleagueId}/invites`, {})
  const parsed = managerSubleagueInviteCreateResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid subleague invite payload', parsed.error.flatten())
    throw new Error('De subleague-invite kon niet worden gemaakt.')
  }

  return {
    inviteUrl: parsed.data.invite_url,
    tokenExpiresAt: parsed.data.token_expires_at,
    maxUses: parsed.data.max_uses,
  }
}

export function useCreateManagerSubleagueInvite(
  leagueId: number | undefined,
  subleagueId: number | undefined,
) {
  return useMutation({
    mutationFn: () => createManagerSubleagueInvite(leagueId as number, subleagueId as number),
  })
}

async function joinManagerLeague(leagueId: number): Promise<{
  membership?: {
    status?: string | null | undefined
    role?: string | null | undefined
    joined_at?: string | null | undefined
  } | undefined
  message?: string | undefined
}> {
  const response = await api.post<unknown>(`/fantasy-leagues/${leagueId}/join`, {})
  const parsed = z
    .object({
      message: z.string().optional(),
      membership: z
        .object({
          status: z.string().nullable().optional(),
          role: z.string().nullable().optional(),
          joined_at: z.string().nullable().optional(),
        })
        .optional(),
    })
    .passthrough()
    .safeParse(response)

  if (!parsed.success || !parsed.data.membership) {
    throw new Error('Deelnemen aan de Manager-league is niet gelukt.')
  }

  return parsed.data
}

export function useJoinManagerLeague(leagueId: number | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => joinManagerLeague(leagueId as number),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['manager-home'] }),
        queryClient.invalidateQueries({ queryKey: ['public-manager-leagues'] }),
        queryClient.invalidateQueries({ queryKey: ['manager-league-detail', leagueId] }),
        queryClient.invalidateQueries({ queryKey: ['manager-league-dashboard', leagueId] }),
      ])
    },
  })
}

async function fetchManagerNextLineup(leagueId: number): Promise<ManagerNextLineup> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/lineup/next`)
  const parsed = managerNextLineupSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid next lineup payload', parsed.error.flatten())
    throw new Error('De volgende Manager-opstelling kon niet worden bepaald.')
  }

  return parsed.data
}

export function useManagerNextLineup(leagueId: number | undefined) {
  return useQuery({
    queryKey: ['manager-next-lineup', leagueId],
    queryFn: () => fetchManagerNextLineup(leagueId!),
    enabled: Boolean(leagueId),
    staleTime: 15_000,
  })
}

async function fetchManagerRounds(leagueId: number): Promise<ManagerRoundsResponse> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/app-rounds`)
  const parsed = managerRoundsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid rounds payload', parsed.error.flatten())
    throw new Error('De Manager-speelrondes konden niet worden geladen.')
  }

  return parsed.data
}

export function useManagerRounds(leagueId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['manager-rounds', leagueId],
    queryFn: () => fetchManagerRounds(leagueId!),
    enabled: Boolean(leagueId && enabled),
    staleTime: 30_000,
  })
}

async function fetchManagerRanking(leagueId: number): Promise<ManagerRankingResponse> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/ranking`)
  const parsed = managerRankingResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid ranking payload', parsed.error.flatten())
    throw new Error('De Manager-ranking kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerRanking(leagueId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['manager-ranking', leagueId],
    queryFn: () => fetchManagerRanking(leagueId!),
    enabled: Boolean(leagueId && enabled),
    staleTime: 15_000,
  })
}

async function fetchManagerPlayerRanking(
  leagueId: number,
  query: string,
  slot: string,
  page: number,
): Promise<ManagerPlayerRankingResponse> {
  const params = new URLSearchParams({ per_page: '50', page: String(page) })
  if (query.trim()) params.set('q', query.trim())
  if (slot.trim()) params.set('slot', slot.trim())

  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/players/ranking?${params.toString()}`)
  const parsed = managerPlayerRankingResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid player ranking payload', parsed.error.flatten())
    throw new Error('De spelersranking kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerPlayerRanking(
  leagueId: number | undefined,
  query: string,
  slot: string,
  page: number,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-player-ranking', leagueId, query.trim(), slot, page],
    queryFn: () => fetchManagerPlayerRanking(leagueId!, query, slot, page),
    enabled: Boolean(leagueId && enabled),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  })
}

async function fetchManagerLeaguePlayerContribution(
  leagueId: number,
  playerId: number,
): Promise<ManagerLeaguePlayerContribution> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/players/${playerId}/contributions`)
  const parsed = managerLeaguePlayerContributionSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid league player contribution payload', parsed.error.flatten())
    throw new Error('De puntenopbouw van deze speler kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerLeaguePlayerContribution(
  leagueId: number | undefined,
  playerId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-league-player-contribution', leagueId, playerId],
    queryFn: () => fetchManagerLeaguePlayerContribution(leagueId!, playerId!),
    enabled: Boolean(leagueId && playerId && enabled),
    staleTime: 30_000,
  })
}

async function fetchManagerPlayerContributions(leagueId: number): Promise<ManagerPlayerContributions> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/players/contributions/me`)
  const parsed = managerPlayerContributionsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid player contributions payload', parsed.error.flatten())
    throw new Error('De punten per speler konden niet worden geladen.')
  }

  return parsed.data
}

async function fetchManagerUserPlayerContributions(
  leagueId: number,
  userId: number,
): Promise<ManagerPlayerContributions> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/users/${userId}/players/contributions`)
  const parsed = managerPlayerContributionsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid user player contributions payload', parsed.error.flatten())
    throw new Error('De punten per speler konden niet worden geladen.')
  }

  return parsed.data
}

export function useManagerPlayerContributions(leagueId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: ['manager-player-contributions', leagueId],
    queryFn: () => fetchManagerPlayerContributions(leagueId!),
    enabled: Boolean(leagueId && enabled),
    staleTime: 30_000,
  })
}

export function useManagerUserPlayerContributions(
  leagueId: number | undefined,
  userId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-user-player-contributions', leagueId, userId],
    queryFn: () => fetchManagerUserPlayerContributions(leagueId!, userId!),
    enabled: Boolean(leagueId && userId && enabled),
    staleTime: 30_000,
  })
}

async function fetchManagerPlayerContributionDetail(
  leagueId: number,
  playerId: number,
): Promise<ManagerPlayerContributionDetail> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/players/${playerId}/contributions/me`)
  const parsed = managerPlayerContributionDetailSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid player contribution detail payload', parsed.error.flatten())
    throw new Error('De spelerbijdrage kon niet worden geladen.')
  }

  return parsed.data
}

async function fetchManagerUserPlayerContributionDetail(
  leagueId: number,
  userId: number,
  playerId: number,
): Promise<ManagerPlayerContributionDetail> {
  const response = await api.get<unknown>(
    `/fantasy-leagues/${leagueId}/users/${userId}/players/${playerId}/contributions`,
  )
  const parsed = managerPlayerContributionDetailSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid user player contribution detail payload', parsed.error.flatten())
    throw new Error('De spelerbijdrage kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerPlayerContributionDetail(
  leagueId: number | undefined,
  playerId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-player-contribution-detail', leagueId, playerId],
    queryFn: () => fetchManagerPlayerContributionDetail(leagueId!, playerId!),
    enabled: Boolean(leagueId && playerId && enabled),
    staleTime: 30_000,
  })
}

export function useManagerUserPlayerContributionDetail(
  leagueId: number | undefined,
  userId: number | undefined,
  playerId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-user-player-contribution-detail', leagueId, userId, playerId],
    queryFn: () => fetchManagerUserPlayerContributionDetail(leagueId!, userId!, playerId!),
    enabled: Boolean(leagueId && userId && playerId && enabled),
    staleTime: 30_000,
  })
}

async function fetchManagerRoundRanking(
  leagueId: number,
  roundId: number,
): Promise<ManagerRoundRankingResponse> {
  const response = await api.get<unknown>(
    `/fantasy-leagues/${leagueId}/app-rounds/${roundId}/ranking`,
  )
  const parsed = managerRoundRankingResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid round ranking payload', parsed.error.flatten())
    throw new Error('De ranking van deze Manager-speelronde kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerRoundRanking(
  leagueId: number | undefined,
  roundId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-round-ranking', leagueId, roundId],
    queryFn: () => fetchManagerRoundRanking(leagueId!, roundId!),
    enabled: Boolean(leagueId && roundId && enabled),
    staleTime: 15_000,
  })
}

async function fetchManagerPlayerRoundBreakdown(
  leagueId: number,
  roundId: number,
  playerId: number,
): Promise<ManagerPlayerRoundBreakdown> {
  const response = await api.get<unknown>(
    `/fantasy-leagues/${leagueId}/app-rounds/${roundId}/players/${playerId}/breakdown`,
  )
  const parsed = managerPlayerRoundBreakdownSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid player round breakdown payload', parsed.error.flatten())
    throw new Error('De puntenopbouw kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerPlayerRoundBreakdown(
  leagueId: number | undefined,
  roundId: number | undefined,
  playerId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-player-round-breakdown', leagueId, roundId, playerId],
    queryFn: () => fetchManagerPlayerRoundBreakdown(leagueId!, roundId!, playerId!),
    enabled: Boolean(leagueId && roundId && playerId && enabled),
    staleTime: 30_000,
  })
}

async function fetchManagerRoundDetail(leagueId: number, roundId: number): Promise<ManagerRoundDetail> {
  const response = await api.get<unknown>(`/fantasy-leagues/${leagueId}/app-rounds/${roundId}`)
  const parsed = managerRoundDetailSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid round detail payload', parsed.error.flatten())
    throw new Error('De bewerkbaarheid van deze Manager-ronde kon niet worden bepaald.')
  }

  return parsed.data
}

export function useManagerRoundDetail(leagueId: number | undefined, roundId: number | undefined) {
  return useQuery({
    queryKey: ['manager-round-detail', leagueId, roundId],
    queryFn: () => fetchManagerRoundDetail(leagueId!, roundId!),
    enabled: Boolean(leagueId && roundId),
    staleTime: 15_000,
  })
}

async function fetchManagerRoundAvailability(
  leagueId: number,
  roundId: number,
): Promise<ManagerRoundAvailability> {
  const response = await api.get<unknown>(
    `/fantasy-leagues/${leagueId}/app-rounds/${roundId}/availability`,
  )
  const parsed = managerRoundAvailabilitySchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid round availability payload', parsed.error.flatten())
    throw new Error('De Manager-opstelling kon niet worden geladen.')
  }

  return parsed.data
}

export function useManagerRoundAvailability(leagueId: number | undefined, roundId: number | undefined) {
  return useQuery({
    queryKey: ['manager-round-availability', leagueId, roundId],
    queryFn: () => fetchManagerRoundAvailability(leagueId!, roundId!),
    enabled: Boolean(leagueId && roundId),
    staleTime: 15_000,
  })
}

async function fetchManagerPlayers({
  leagueId,
  roundId,
  slot,
  teamId,
  sort,
  query,
  replacePlayerId,
}: {
  leagueId: number
  roundId: number
  slot: string
  teamId: number | undefined
  sort: string
  query: string
  replacePlayerId: number | undefined
}): Promise<ManagerPlayersResponse> {
  const params = new URLSearchParams({ slot })
  if (teamId) params.set('team_id', String(teamId))
  if (sort.trim()) params.set('sort', sort.trim())
  if (query.trim()) params.set('q', query.trim())
  if (replacePlayerId) params.set('replace_player_id', String(replacePlayerId))
  const response = await api.get<unknown>(
    `/fantasy-leagues/${leagueId}/app-rounds/${roundId}/players?${params.toString()}`,
  )
  const parsed = managerPlayersResponseSchema.safeParse(response)

  if (!parsed.success) throw new Error('De spelers konden niet worden geladen.')
  return parsed.data
}

export function useManagerPlayers({
  leagueId,
  roundId,
  slot,
  teamId,
  sort,
  query,
  replacePlayerId,
}: {
  leagueId: number | undefined
  roundId: number | undefined
  slot: string | undefined
  teamId: number | undefined
  sort: string
  query: string
  replacePlayerId: number | undefined
}) {
  return useQuery({
    queryKey: ['manager-players', leagueId, roundId, slot, teamId, sort, query, replacePlayerId],
    queryFn: () =>
      fetchManagerPlayers({
        leagueId: leagueId!,
        roundId: roundId!,
        slot: slot!,
        teamId,
        sort,
        query,
        replacePlayerId,
      }),
    enabled: Boolean(leagueId && roundId && slot),
    staleTime: 15_000,
  })
}

async function fetchManagerLineupSuggestions(
  leagueId: number,
  roundId: number,
): Promise<ManagerLineupSuggestions> {
  const response = await api.get<unknown>(
    `/fantasy-leagues/${leagueId}/app-rounds/${roundId}/lineup/me/suggestions`,
  )
  const parsed = managerLineupSuggestionsSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[manager] Invalid lineup suggestions payload', parsed.error.flatten())
    throw new Error('Wisselsuggesties konden niet worden geladen.')
  }

  return parsed.data
}

export function useManagerLineupSuggestions(
  leagueId: number | undefined,
  roundId: number | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['manager-lineup-suggestions', leagueId, roundId],
    queryFn: () => fetchManagerLineupSuggestions(leagueId!, roundId!),
    enabled: Boolean(leagueId && roundId && enabled),
    staleTime: 15_000,
  })
}

export function useApplyManagerLineupSuggestion() {
  return useMutation({
    mutationFn: ({
      leagueId,
      roundId,
      outgoingPlayerId,
      incomingPlayerId,
    }: {
      leagueId: number
      roundId: number
      outgoingPlayerId: number
      incomingPlayerId: number
    }) =>
      api.post<unknown>(
        `/fantasy-leagues/${leagueId}/app-rounds/${roundId}/lineup/me/suggestions/apply`,
        {
          outgoing_player_id: outgoingPlayerId,
          incoming_player_id: incomingPlayerId,
        },
      ),
  })
}

export function useIgnoreManagerLineupSuggestion() {
  return useMutation({
    mutationFn: ({
      leagueId,
      roundId,
      outgoingPlayerId,
      incomingPlayerId,
    }: {
      leagueId: number
      roundId: number
      outgoingPlayerId: number
      incomingPlayerId: number
    }) =>
      api.post<unknown>(
        `/fantasy-leagues/${leagueId}/app-rounds/${roundId}/lineup/me/suggestions/ignore`,
        {
          outgoing_player_id: outgoingPlayerId,
          incoming_player_id: incomingPlayerId,
        },
      ),
  })
}

export function useSaveManagerDraft() {
  return useMutation({
    mutationFn: ({
      leagueId,
      roundId,
      formation,
      players,
      draftVersion,
    }: {
      leagueId: number
      roundId: number
      formation: string
      players: Array<{ slot: string; slot_index: number; player_id: number }>
      draftVersion?: number
    }) =>
      api.put(`/fantasy-leagues/${leagueId}/app-rounds/${roundId}/lineup/draft`, {
        formation,
        players,
        ...(draftVersion === undefined ? {} : { draft_version: draftVersion }),
      }),
  })
}

export function useSubmitManagerLineup() {
  return useMutation({
    mutationFn: ({
      leagueId,
      roundId,
      formation,
      players,
    }: {
      leagueId: number
      roundId: number
      formation: string
      players: Array<{ slot: string; slot_index: number; player_id: number }>
    }) =>
      api.put(`/fantasy-leagues/${leagueId}/app-rounds/${roundId}/lineup`, {
        formation,
        players,
      }),
  })
}

export function useRestoreManagerPreviousLineup() {
  return useMutation({
    mutationFn: ({ leagueId, roundId }: { leagueId: number; roundId: number }) =>
      api.post<unknown>(
        `/fantasy-leagues/${leagueId}/app-rounds/${roundId}/lineup/reuse-previous`,
        {},
      ),
  })
}
